import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Flex, Popconfirm, Row, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { BrandingContentControls } from '../../public-form/components/branding-content-controls'
import { BrandingPreview } from '../../public-form/components/branding-preview'
import { BrandingStyleControls } from '../../public-form/components/branding-style-controls'
import { Configuration, defaultConfiguration, PreviewAppearance } from '../../public-form/public-form'
import { uploadSiteLogo } from '../api'
import { useFormStyle, useUpdateFormStyle } from '../hooks'

/** What the site's style covers; the texts stay per form. */
type Style = Pick<Configuration, 'brandName' | 'logoUrl' | 'appearance' | 'theme' | 'darkTheme'>

function pickStyle(value: Configuration): Style {
    return {
        brandName: value.brandName,
        logoUrl: value.logoUrl,
        appearance: value.appearance,
        theme: value.theme,
        darkTheme: value.darkTheme,
    }
}

/**
 * The look of the website's apply forms: new screenings get it, and it can be given to all existing ones. The texts
 * stay per vacancy. WordPress websites can also set it in wp-admin.
 */
export function SiteFormStyle({ siteId }: { siteId: string }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data } = useFormStyle(siteId)
    const update = useUpdateFormStyle(siteId)
    const [value, setValue] = useState<Configuration>()
    const [previewAppearance, setPreviewAppearance] = useState<PreviewAppearance>('light')
    const [device, setDevice] = useState('Desktop')

    const saved = useMemo(
        () =>
            data
                ? ({
                      ...defaultConfiguration(t('Nurse'), ''),
                      introduction: t('Answer a few questions on WhatsApp. It takes about five minutes.'),
                      ...(data.style as Partial<Configuration>),
                  } as Configuration)
                : undefined,
        [data, t]
    )

    useEffect(() => {
        if (saved) {
            setValue(saved)
        }
    }, [saved])

    if (!value || !saved || !data) {
        return <Skeleton active />
    }

    const editable = can(UPDATE_SITE)
    const set = (patch: Partial<Configuration>) => setValue({ ...value, ...patch })
    const changed = JSON.stringify(pickStyle(value)) !== JSON.stringify(pickStyle(saved))
    const activeAppearance = value.appearance === 'system' ? previewAppearance : value.appearance
    const save = (apply: boolean) => update.mutate({ style: pickStyle(value), apply })

    return (
        <Row gutter={[24, 24]}>
            <Col
                xs={24}
                xl={11}
            >
                <Flex
                    vertical
                    gap="large"
                >
                    <Card title={t('Apply form')}>
                        <Flex
                            vertical
                            gap="middle"
                        >
                            <Typography.Text type="secondary">
                                {t(
                                    'Choose how your apply forms look: logo, colours and font. Vacancies where you switch on applying via WhatsApp get this style by themselves. You change the texts per vacancy.'
                                )}
                            </Typography.Text>
                            <BrandingContentControls
                                value={value}
                                set={set}
                                onUpload={editable ? (file) => uploadSiteLogo(siteId, file) : undefined}
                                brandOnly
                            />
                        </Flex>
                    </Card>
                    <Card title={t('Style')}>
                        <BrandingStyleControls
                            value={value}
                            set={set}
                            previewAppearance={previewAppearance}
                            setPreviewAppearance={setPreviewAppearance}
                        />
                    </Card>
                    {editable && (
                        <Flex
                            justify="end"
                            gap="small"
                            wrap
                        >
                            {data.forms > 0 && (
                                <Popconfirm
                                    title={t('Use this style for all {{count}} forms?', { count: data.forms })}
                                    description={t('The forms keep their own texts.')}
                                    onConfirm={() => save(true)}
                                >
                                    <Button loading={update.isPending && update.variables?.apply}>
                                        {t('Save and use for all forms')}
                                    </Button>
                                </Popconfirm>
                            )}
                            <Button
                                type="primary"
                                disabled={!changed && data.saved}
                                loading={update.isPending && !update.variables?.apply}
                                onClick={() => save(false)}
                            >
                                {t('Save')}
                            </Button>
                        </Flex>
                    )}
                </Flex>
            </Col>
            <Col
                xs={24}
                xl={13}
            >
                <Card className="xl:sticky xl:top-4">
                    <BrandingPreview
                        value={value}
                        activeAppearance={activeAppearance}
                        device={device}
                        onDeviceChange={setDevice}
                    />
                </Card>
            </Col>
        </Row>
    )
}
